import { type ReactNode, type HTMLAttributes, useMemo } from "react";
import { useId } from "react";
import styled from "@emotion/styled";

import {
  space,
  color,
  fontSize,
  fontWeight,
  lineHeight,
  variant,
} from "styled-system";

import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { InfoOutlinedIcon } from "src/lib/ui-icon-v2";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { ControlLabelV2Provider } from "src/components/ControlLabelV2/ControlLabelV2.context.ts";

type PlacementVariation = "top" | "bottom" | "start" | "end";

type BaseControlLabelProps = Omit<HTMLAttributes<HTMLDivElement>, "color"> & {
  control: ReactNode & { props?: object };
  label: ReactNode;
  helperText?: ReactNode;
  labelPlacement?: PlacementVariation;
  id?: string;
  isLoading?: boolean;
  disabled?: boolean;
};

export type ControlLabelV2Props = BaseControlLabelProps;

const BaseControlLabel = (props: ControlLabelV2Props) => {
  const {
    control,
    label,
    helperText,
    disabled = false,
    isLoading = false,
    id: idProp,
    ...restProps
  } = props;

  const localId = useId();
  const id = idProp ?? localId;

  const context = useMemo(
    () => ({
      disabled,
      id,
    }),
    [disabled, id],
  );

  return (
    <ControlLabelV2Provider value={context}>
      <Skeleton isLoading={isLoading}>
        <div css={containerStyles({ disabled })} {...restProps}>
          {control}
          <div css={labelContainerStyles}>
            <label htmlFor={id} css={labelStyles({ disabled })}>
              {label}
            </label>
            {helperText && (
              <TooltipV2 cursor={"help"} content={helperText}>
                <InfoOutlinedIcon css={helperTextStyles} />
              </TooltipV2>
            )}
          </div>
        </div>
      </Skeleton>
    </ControlLabelV2Provider>
  );
};

const labelStyles = ({ disabled }: { disabled: boolean }) => css`
  cursor: ${disabled ? "default" : "pointer"};
  user-select: none;
`;

const placementStyling: Record<PlacementVariation, object> = {
  top: { flexDirection: "column-reverse" },
  bottom: { flexDirection: "column" },
  start: { flexDirection: "row-reverse" },
  end: { flexDirection: "row" },
};

export const ControlLabelV2 = styled(BaseControlLabel)`
  ${space}
  ${fontSize}
  ${fontWeight}
  ${lineHeight}
  ${color}
  ${variant({ prop: "labelPlacement", variants: placementStyling })}
`;

const labelContainerStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const helperTextStyles = (theme: ExactTheme) => css`
  color: ${theme.colors.fg.muted};
  font-size: 12px;
`;

const containerStyles =
  ({ disabled }: { disabled: boolean }) =>
  (theme: ExactTheme) => css`
    display: inline-flex;
    align-items: center;
    width: auto;
    gap: 10px;
    line-height: 165%;
    font-size: ${theme.fontSizes[1]};
    opacity: ${disabled ? 0.2 : 1};
  `;
